import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase, fetchUserCredits } from './services/supabase';
import { readEditedImageHandoff, setPendingEditedHandoff } from './services/photoedit-handoff';
import {
  loadPersistedPhotoplanReturnContext,
  persistPhotoplanReturnContext,
  readPhotoplanReturnContext,
  type PhotoplanReturnContext,
} from './services/photoplan-return';
import { Loader2 } from 'lucide-react';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import HyperDuskProject from './components/HyperDuskProject';
import ProjectDetail from './components/ProjectDetail';
import Header from './components/Header';
import LandingPage from './components/LandingPage';
import Footer from './components/Footer';
import FluidBackground from './components/FluidBackground';
import ShareDemoPage from '../photoclear/components/ShareDemoPage';

type AppView = 'LOGIN' | 'DASHBOARD' | 'NEW_PROJECT' | 'PROJECT_DETAIL';
type AuthMode = 'login' | 'signup';

// Internal deep-link: /p/{projectId} opens that project's detail view for the
// signed-in team, so a project URL can be copied from the address bar and
// shared internally (or bookmarked / opened in a new tab).
function getDeepLinkProjectId(): string | null {
  const match = window.location.pathname.match(/^\/p\/([0-9a-fA-F-]+)$/);
  return match ? match[1] : null;
}

export default function App() {
  const [view, setView] = useState<AppView>('LOGIN');
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [userCredits, setUserCredits] = useState(0);
  const [booting, setBooting] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [mouseGlow, setMouseGlow] = useState({ x: 0, y: 0, active: false });
  const [incomingImageUrl, setIncomingImageUrl] = useState<string | null>(null);
  const [photoplanReturn, setPhotoplanReturn] = useState<PhotoplanReturnContext | null>(null);
  const intentionalSignOutRef = useRef(false);
  // Tracks the user we've already landed/navigated for. Supabase fires a fresh
  // SIGNED_IN event whenever the tab is refocused; without this guard that would
  // re-run landAfterAuth and bounce the user back to the dashboard mid-task.
  const authedUserIdRef = useRef<string | null>(null);
  const editedHandoffRef = useRef<ReturnType<typeof readEditedImageHandoff>>(null);

  const loadCredits = useCallback(async (userId: string) => {
    const credits = await fetchUserCredits(userId);
    setUserCredits(credits);
  }, []);

  const navigate = useCallback((target: AppView, projectId?: string) => {
    if (projectId) setSelectedProjectId(projectId);
    setView(target);
    // Reflect an open project in the address bar as /p/{id} so the URL is a
    // shareable internal project link; reset to / otherwise.
    const path = target === 'PROJECT_DETAIL' && projectId ? `/p/${projectId}` : '/';
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }
  }, []);

  // Land the authenticated user on the right screen. A PhotoEdit return or
  // /p/{id} deep link wins, then the cross-app image handoff, otherwise dashboard.
  const landAfterAuth = useCallback((preferNewProject: boolean) => {
    const edited = editedHandoffRef.current;
    if (edited) {
      setSelectedProjectId(edited.projectId);
      setView('PROJECT_DETAIL');
      return;
    }
    const pid = getDeepLinkProjectId();
    if (pid && !preferNewProject) {
      setSelectedProjectId(pid);
      setView('PROJECT_DETAIL');
      return;
    }
    setView(preferNewProject ? 'NEW_PROJECT' : 'DASHBOARD');
  }, []);

  useEffect(() => {
    let cancelled = false;
    let callbackTimer: ReturnType<typeof setTimeout> | null = null;
    let bootSafetyTimer: ReturnType<typeof setTimeout> | null = null;
    let recoveryTimer: ReturnType<typeof setTimeout> | null = null;
    let recoveryAttempts = 0;

    const safeLoadCredits = (userId: string) => {
      void loadCredits(userId).catch((err) => {
        console.warn('Hyper Dusk credit load failed:', err);
      });
    };

    const attemptSessionRecovery = async () => {
      if (cancelled) return;
      recoveryAttempts += 1;
      try {
        const { data: refreshed } = await supabase.auth.refreshSession();
        const recoveredSession = refreshed.session ?? (await supabase.auth.getSession()).data.session;
        if (recoveredSession) {
          setSession(recoveredSession);
          setUser(recoveredSession.user);
          authedUserIdRef.current = recoveredSession.user.id;
          safeLoadCredits(recoveredSession.user.id);
          recoveryAttempts = 0;
          return;
        }
      } catch {
        // Continue retry flow below.
      }

      if (recoveryAttempts < 8) {
        const delay = Math.min(2000 * Math.pow(1.5, recoveryAttempts - 1), 15000);
        recoveryTimer = setTimeout(() => {
          void attemptSessionRecovery();
        }, delay);
        return;
      }

      recoveryAttempts = 0;
      authedUserIdRef.current = null;
      setSession(null);
      setUser(null);
      setView('LOGIN');
    };

    const initAuth = async () => {
      try {
        // Parse incoming image URL and SSO tokens before cleaning the URL
        const incomingSearch = new URLSearchParams(window.location.search);
        const imageUrlParam = incomingSearch.get('imageUrl');
        const photoplanCtx = readPhotoplanReturnContext() ?? loadPersistedPhotoplanReturnContext();
        if (photoplanCtx) {
          persistPhotoplanReturnContext(photoplanCtx);
          setPhotoplanReturn(photoplanCtx);
        }
        const editedHandoff = readEditedImageHandoff();
        const incomingHash = new URLSearchParams(window.location.hash.substring(1));
        const ssoAccessToken = incomingHash.get('sso_access_token');
        const ssoRefreshToken = incomingHash.get('sso_refresh_token');

        if (imageUrlParam) {
          setIncomingImageUrl(imageUrlParam);
        }
        if (editedHandoff) {
          editedHandoffRef.current = editedHandoff;
          setPendingEditedHandoff(editedHandoff);
          setSelectedProjectId(editedHandoff.projectId);
        }

        // Clean query/hash; keep /p/{id} when returning an edited still.
        if (imageUrlParam || ssoAccessToken || editedHandoff) {
          const cleanPath = editedHandoff ? `/p/${editedHandoff.projectId}` : window.location.pathname;
          window.history.replaceState({}, document.title, cleanPath);
        }

        if (ssoAccessToken && ssoRefreshToken) {
          try {
            const { data, error } = await supabase.auth.setSession({
              access_token: ssoAccessToken,
              refresh_token: ssoRefreshToken,
            });
            if (cancelled) return;
            if (data.session) {
              setSession(data.session);
              setUser(data.session.user);
              authedUserIdRef.current = data.session.user.id;
              safeLoadCredits(data.session.user.id);
              landAfterAuth(!!imageUrlParam);
              setBooting(false);
              return;
            } else if (error) {
              console.warn('Hyper Dusk SSO session failed:', error.message);
            }
          } catch (err) {
            console.warn('Hyper Dusk SSO session error:', err);
          }
        }

        const isAuthCallback = window.location.pathname === '/auth/callback';
        if (isAuthCallback) {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const searchParams = new URLSearchParams(window.location.search);
          const callbackError =
            hashParams.get('error_description') ||
            hashParams.get('error') ||
            searchParams.get('error_description') ||
            searchParams.get('error');

          if (callbackError) {
            window.history.replaceState({}, document.title, '/');
            setView('LOGIN');
            return;
          }

          callbackTimer = setTimeout(() => {
            if (cancelled) return;
            window.history.replaceState({}, document.title, '/');
            setView('LOGIN');
            setBooting(false);
          }, 8000);

          const code = searchParams.get('code');
          if (code) {
            try {
              const { data, error } = await supabase.auth.exchangeCodeForSession(code);
              if (cancelled) return;
              if (error) {
                console.warn('Hyper Dusk callback code exchange failed:', error.message);
              } else if (data.session) {
                window.history.replaceState({}, document.title, '/');
                setSession(data.session);
                setUser(data.session.user);
                authedUserIdRef.current = data.session.user.id;
                safeLoadCredits(data.session.user.id);
                setView('DASHBOARD');
                return;
              }
            } catch (err) {
              console.warn('Hyper Dusk callback exchange error:', err);
            }
          }
        }

        const sessionResult = await Promise.race([
          supabase.auth.getSession(),
          new Promise<{ data: { session: null } }>((resolve) =>
            setTimeout(() => resolve({ data: { session: null } }), 6000)
          ),
        ]);
        const { data: { session: existingSession } } = sessionResult;
        if (cancelled) return;

        if (existingSession) {
          if (window.location.pathname === '/auth/callback') {
            window.history.replaceState({}, document.title, '/');
          }
          setSession(existingSession);
          setUser(existingSession.user);
          authedUserIdRef.current = existingSession.user.id;
          safeLoadCredits(existingSession.user.id);
          landAfterAuth(!!imageUrlParam);
        } else if (!isAuthCallback) {
          setView('LOGIN');
        }
      } catch (err) {
        console.warn('Hyper Dusk auth init failed:', err);
        if (!cancelled) {
          setView('LOGIN');
        }
      } finally {
        if (!cancelled) {
          setBooting(false);
        }
      }
    };

    initAuth();
    bootSafetyTimer = setTimeout(() => {
      if (cancelled) return;
      // Final guard: only act if boot is still in progress.
      setBooting((isStillBooting) => {
        if (!isStillBooting) return isStillBooting;
        if (window.location.pathname === '/auth/callback') {
          window.history.replaceState({}, document.title, '/');
        }
        setView('LOGIN');
        return false;
      });
    }, 12000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (cancelled) return;

      switch (event) {
        case 'SIGNED_IN': {
          recoveryAttempts = 0;
          // Cancel the /auth/callback safety timeout — otherwise a successful
          // Google/magic-link sign-in still gets forced to LOGIN ~8s later.
          if (callbackTimer) {
            clearTimeout(callbackTimer);
            callbackTimer = null;
          }
          setSession(newSession);
          setUser(newSession?.user ?? null);
          if (window.location.pathname === '/auth/callback') {
            window.history.replaceState({}, document.title, '/');
          }
          if (newSession?.user) {
            // Supabase re-emits SIGNED_IN on tab refocus. Only land/navigate the
            // first time we see this user; otherwise just refresh credits and
            // leave the current view (and any in-progress work) untouched.
            const isNewSignIn = authedUserIdRef.current !== newSession.user.id;
            authedUserIdRef.current = newSession.user.id;
            safeLoadCredits(newSession.user.id);
            if (isNewSignIn) {
              landAfterAuth(false);
            }
          } else {
            authedUserIdRef.current = null;
            setView('LOGIN');
          }
          break;
        }

        case 'TOKEN_REFRESHED':
          recoveryAttempts = 0;
          setSession(newSession);
          setUser(newSession?.user ?? null);
          if (newSession?.user) {
            authedUserIdRef.current = newSession.user.id;
            safeLoadCredits(newSession.user.id);
          }
          break;

        case 'INITIAL_SESSION':
          recoveryAttempts = 0;
          setSession(newSession);
          setUser(newSession?.user ?? null);
          if (newSession?.user) {
            authedUserIdRef.current = newSession.user.id;
          }
          // Avoid bouncing to login while callback exchange is still underway.
          if (newSession?.user && window.location.pathname === '/auth/callback') {
            window.history.replaceState({}, document.title, '/');
            safeLoadCredits(newSession.user.id);
            setView('DASHBOARD');
          }
          break;

        case 'SIGNED_OUT':
          if (intentionalSignOutRef.current) {
            intentionalSignOutRef.current = false;
            recoveryAttempts = 0;
            authedUserIdRef.current = null;
            setSession(null);
            setUser(null);
            setView('LOGIN');
            break;
          }
          // Only attempt recovery if we had an active session — transient
          // SIGNED_OUT events from token refresh glitches should not log out.
          setSession((prev) => {
            if (!prev) {
              setUser(null);
              setView('LOGIN');
              return null;
            }
            if (recoveryTimer) clearTimeout(recoveryTimer);
            recoveryTimer = setTimeout(() => {
              void attemptSessionRecovery();
            }, 1500);
            return prev;
          });
          break;
      }

      setBooting(false);
    });

    return () => {
      cancelled = true;
      if (callbackTimer) clearTimeout(callbackTimer);
      if (bootSafetyTimer) clearTimeout(bootSafetyTimer);
      if (recoveryTimer) clearTimeout(recoveryTimer);
      subscription.unsubscribe();
    };
  }, [loadCredits, landAfterAuth]);

  // Keep the view in sync with browser back/forward across /p/{id} links.
  useEffect(() => {
    if (!user) return;
    const onPop = () => {
      const pid = getDeepLinkProjectId();
      if (pid) {
        setSelectedProjectId(pid);
        setView('PROJECT_DETAIL');
      } else {
        setView((cur) => (cur === 'PROJECT_DETAIL' ? 'DASHBOARD' : cur));
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [user]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      setMouseGlow({ x: e.clientX, y: e.clientY, active: true });
    };
    const onLeave = () => {
      setMouseGlow((prev) => ({ ...prev, active: false }));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseleave', onLeave);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  const handleSignOut = async () => {
    intentionalSignOutRef.current = true;
    authedUserIdRef.current = null;
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setView('LOGIN');
  };

  const handleBuyCredits = async () => {
    const hubUrl = 'https://hub.rehub.studio';
    try {
      const { data: { session: activeSession } } = await supabase.auth.getSession();
      if (activeSession?.access_token && activeSession?.refresh_token) {
        window.location.href = `${hubUrl}#sso_access_token=${activeSession.access_token}&sso_refresh_token=${activeSession.refresh_token}`;
        return;
      }
    } catch {
      // Fall through to direct pricing page.
    }
    window.location.href = 'https://hub.rehub.studio/#/pricing';
  };

  const shareMatch = window.location.pathname.match(/^\/share\/([a-zA-Z0-9_-]+)$/);
  if (shareMatch) {
    return <ShareDemoPage slug={shareMatch[1]} />;
  }

  if (booting) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (view === 'LOGIN') {
    return (
      <div className="min-h-screen bg-black text-slate-100 flex flex-col relative overflow-hidden">
        <FluidBackground />
        <div className="pointer-events-none fixed inset-0 z-0 bg-[radial-gradient(circle_at_50%_12%,rgba(255,125,92,0.22),transparent_42%),radial-gradient(circle_at_82%_22%,rgba(211,72,128,0.2),transparent_48%),radial-gradient(circle_at_18%_24%,rgba(120,72,221,0.18),transparent_50%),linear-gradient(180deg,#12080f_0%,#0f0814_46%,#06050a_100%)]" />
        <div
          className={`pointer-events-none fixed inset-0 z-0 transition-opacity duration-200 ${mouseGlow.active ? 'opacity-100' : 'opacity-0'}`}
          style={{
            background: `radial-gradient(circle 340px at ${mouseGlow.x}px ${mouseGlow.y}px, rgba(255, 141, 95, 0.24), rgba(216, 86, 197, 0.14) 38%, rgba(94, 92, 255, 0.08) 62%, transparent 78%)`,
          }}
        />
        <Header
          onLogin={() => {
            setAuthMode('login');
            setShowAuthModal(true);
          }}
          onSignUp={() => {
            setAuthMode('signup');
            setShowAuthModal(true);
          }}
        />
        <div className="relative z-10 flex-1">
          <LandingPage
            onLogin={() => {
              setAuthMode('login');
              setShowAuthModal(true);
            }}
            onSignUp={() => {
              setAuthMode('signup');
              setShowAuthModal(true);
            }}
          />
        </div>
        <Footer />
        {showAuthModal && (
          <div className="fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => setShowAuthModal(false)}
            />
            <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
              <div className="w-full max-w-md">
                <Login
                  initialMode={authMode}
                  onAuthSuccess={() => {
                    setShowAuthModal(false);
                    landAfterAuth(false);
                  }}
                  onClose={() => setShowAuthModal(false)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-slate-100">
      <Header
        userEmail={session?.user?.email}
        userCredits={userCredits}
        onSignOut={handleSignOut}
        onBuyCredits={handleBuyCredits}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {view === 'DASHBOARD' && (
          <Dashboard onNavigate={navigate} currentUserId={user?.id ?? null} />
        )}
        {view === 'NEW_PROJECT' && user && (
          <HyperDuskProject
            userId={user.id}
            userCredits={userCredits}
            onNavigate={navigate}
            onCreditsUpdated={setUserCredits}
            onBuyCredits={handleBuyCredits}
            initialImageUrl={incomingImageUrl ?? undefined}
            onImageConsumed={() => setIncomingImageUrl(null)}
            photoplanReturn={photoplanReturn}
          />
        )}
        {view === 'PROJECT_DETAIL' && selectedProjectId && (
          <ProjectDetail
            projectId={selectedProjectId}
            onNavigate={navigate}
            currentUserId={user?.id ?? null}
            userCredits={userCredits}
            onCreditsUpdated={setUserCredits}
            photoplanReturn={photoplanReturn}
          />
        )}
      </main>
    </div>
  );
}
